import React, {useEffect} from "react";
import {FormField, Grid, Select, SelectProps} from "@amzn/awsui-components-react/polaris";
import CONSTANTS, { IS_TESTED_PARTY_REPORT_ENABLED } from "src/utils/constants";
import ServiceCollection from "src/services/ServiceCollection";
import {accountingDaysList} from "src/models/reports/GetAccountingDayList";

type ComponentProps = {
    services: ServiceCollection,
    selectedCompanyCode: SelectProps.Option | null,
    selectedPeriodOption: SelectProps.Option | null,
    selectedAccountingDayOption: SelectProps.Option | null,
    onCompanyCodeSelected: (option: SelectProps.Option | null) => void,
    onPeriodSelected: (option: SelectProps.Option | null) => void,
    onAccountingDaySelected: (option: SelectProps.Option | null) => void,
    // When true (event-driven redesign on), Period + Accounting Day come from the dashboard, so only the
    // entity selector is shown. Defaults to false so the legacy three-dropdown layout is unchanged.
    hidePeriodAndDay?: boolean,
    // When true, the reporting-period fetch is skipped. The view sets this while the feature flag is
    // unresolved AND when the redesign is on, so the periods call never fires needlessly.
    skipPeriods?: boolean
}

export default function TPTaxLefReportFilterSelector(props: ComponentProps) {
    const {
        services,
        selectedCompanyCode,
        selectedPeriodOption,
        selectedAccountingDayOption,
        onCompanyCodeSelected,
        onPeriodSelected,
        onAccountingDaySelected
    } = props;

    const [companyDataResult, companyDataLoading, companyDataError] = services.reportsService.getCOADataBySegment(CONSTANTS.COA_SEGMENT_MAPPING.COMPANY.WEB_API)
    // Skip the reporting-period fetch when the period dropdown is hidden / the flag is still resolving.
    const [accountingPeriodResult, accountingPeriodLoading, accountingPeriodError] = services.reportsService.getReportingPeriods(!!props.skipPeriods);

    useEffect(() => {
        if (!accountingPeriodError) {
            return;
        }
        props.services.messageService.showErrorAutoDismissBanner(accountingPeriodError);
    }, [accountingPeriodError]);

    useEffect(() => {
        if (!companyDataError) {
            return;
        }
        props.services.messageService.showErrorAutoDismissBanner(companyDataError);
    }, [companyDataError]);


    const entityField = (
        <FormField label={IS_TESTED_PARTY_REPORT_ENABLED ? "Tested Party Entity" : "Legal Entity"}>
            <Select
                statusType={companyDataLoading ? "loading" : "finished"}
                loadingText={IS_TESTED_PARTY_REPORT_ENABLED ? "Fetching Tested Party Entities" : "Fetching Legal Entities Data"}
                selectedOption={selectedCompanyCode ? {
                    label: selectedCompanyCode.label,
                    value: selectedCompanyCode.value
                } : null}
                onChange={({detail}) =>
                    onCompanyCodeSelected(detail.selectedOption)
                }
                options={
                    companyDataResult == null
                        ? []
                        : companyDataResult.map(companyData => ({
                            label: `${companyData.codeId} - ${companyData.codeName}`,
                            value: companyData.codeId
                        }))
                }
                placeholder="Select Option"
                virtualScroll={true}
                filteringType="auto"
            />
        </FormField>
    );

    // Dashboard-driven layout: only the entity selector (period + day come from the URL).
    if (props.hidePeriodAndDay) {
        return entityField;
    }

    return (
        <Grid
            gridDefinition={[{colspan: 6}, {colspan: 3}, {colspan: 3}]}
        >
            {entityField}
            <FormField label="Period">
                <Select
                    statusType={accountingPeriodLoading ? "loading" : "finished"}
                    loadingText="Fetching Accounting Periods"
                    selectedOption={selectedPeriodOption ? {
                        label: selectedPeriodOption.label,
                        value: selectedPeriodOption.value
                    } : null}
                    onChange={({detail}) =>
                        onPeriodSelected(detail.selectedOption)
                    }
                    options={
                        accountingPeriodResult == null
                            ? []
                            : accountingPeriodResult.ReportingPeriodList.map(
                                (accountingPeriodNameValue) => ({
                                    label: accountingPeriodNameValue,
                                    value: accountingPeriodNameValue,
                                })
                            )
                    }
                    placeholder="Select Option"
                    virtualScroll={true}
                    filteringType="auto"
                />
            </FormField>
            <FormField label="Accounting Day">
                <Select
                    selectedOption={selectedAccountingDayOption ? {
                        label: selectedAccountingDayOption.label,
                        value: selectedAccountingDayOption.value
                    } : null}
                    options={accountingDaysList}
                    onChange={({detail}) => {
                        onAccountingDaySelected(detail.selectedOption);
                    }}
                    placeholder="Select Option"
                />
            </FormField>
        </Grid>
    );
}